import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { Task } from '@/models/task.model';
import { OidcSecurityService } from 'angular-auth-oidc-client';

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private http = inject(HttpClient);
  private oidcSecurityService = inject(OidcSecurityService);
  private apiTaskUrl = `/api/task`;
  private apiOpportunityUrl = `/api/opportunity`;

  createTask(taskData: Partial<Task>): Observable<Task> {
    const url = taskData.opportunityId ? `${this.apiOpportunityUrl}/${taskData.opportunityId}/tasks` : this.apiTaskUrl;

    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.post<Task>(url, taskData, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
    );
  }

  getTaskById(id: number): Observable<Task> {
    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.get<Task>(`${this.apiTaskUrl}/${id}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
    );
  }

  getTasksByOpportunityId(opportunityId: number): Observable<Task[]> {
    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.get<Task[]>(`${this.apiOpportunityUrl}/${opportunityId}/tasks`, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
    );
  }

  getTasksByWalkId(walkId: number): Observable<Task[]> {
    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.get<Task[]>(`${this.apiTaskUrl}/by-walk/${walkId}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
    );
  }

  updateTask(id: number, changes: Partial<Task>): Observable<Task> {
    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.patch<Task>(`${this.apiTaskUrl}/${id}`, changes, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
    );
  }

  deleteTask(id: number): Observable<void> {
    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.delete<void>(`${this.apiTaskUrl}/${id}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
    );
  }

  getStandaloneTasks(): Observable<Task[]> {
    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.get<Task[]>(this.apiTaskUrl, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
    );
  }
}
